import './LuckyWheel.less';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import wheelSound from './sounds/wheel.mp3';
import clsx from 'clsx';
import { LuckyWheelNeedle } from '../../icons/LuckyWheelNeedle';
import i18n from '../../common/i18n';
import { adjustColor, countRandomDegree, countRotation, playAudio } from './utils/utils';
import luckyWheelButton from './images/lucky-wheel-button.png';
import CloseIcon from '../../icons/CloseIcon';

const WheelComponent = ({
    devicePixelRatio,
    segments,
    fontColor,
    onFinished,
    winningSegment,
    size,
    spinTime,
    fontFamily = "'Roboto', 'Twemoji Country Flags', Arial, sans-serif",
    fontFamilyAlternative = "'Roboto Condensed', 'Twemoji Country Flags', Arial, sans-serif",
    title,
    subTitle,
    fontSize,
    onPhone,
    handleInfoClick,
    handleClose,
    angle,
    setAngle,
    disabled,
    spin,
    error
}) => {
    const [isDrawing, setIsDrawing] = useState(true);
    const canvasRef = useRef();
    const segmentsLength = segments.length;
    const ratioSize = useMemo(() => Math.floor(size * devicePixelRatio), [size, devicePixelRatio]);
    const ratioFontSize = useMemo(() => `${fontSize * devicePixelRatio}px`, [devicePixelRatio, fontSize]);
    const countDimensions = useMemo(() => 2 * ratioSize + 60, [ratioSize]);
    const centerX = useMemo(() => ratioSize + 30, [ratioSize]);
    const centerY = useMemo(() => ratioSize + 30, [ratioSize]);
    const randomAngleDeflection = useMemo(() => countRandomDegree(segmentsLength), [segmentsLength]);

    useEffect(() => {
        wheelInit();
        setIsDrawing(false);
        // eslint-disable-next-line
    }, [devicePixelRatio]);

    useEffect(() => {
        if (segments && winningSegment && !winningSegment.duplicateSpin) {
            const audio = playAudio(wheelSound);
            const winningSegmetIndex = segments.findIndex(segment => {
                return segment.id === winningSegment.winSegmentId;
            });
            console.log(segments, winningSegment);
            const angleOfWinningSegment = winningSegmetIndex * (360 / segmentsLength);
            setAngle(360 * spinTime - angleOfWinningSegment + randomAngleDeflection - 180 / segmentsLength);
            setTimeout(() => {
                audio.pause();
                onFinished(winningSegment);
            }, spinTime * 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [winningSegment]);

    function wheelInit() {
        const canvasContext = canvasRef.current.getContext('2d');
        wheelDraw(canvasContext);
    }

    const wheelDraw = canvasContext => {
        const angleCurrent = -Math.PI / 2;
        clear(canvasContext);
        drawWheel(canvasContext, angleCurrent);
    };

    const segmentNameStyle = (ctx, segmentWinType, fontSize) => {
        ctx.font = `bold ${segmentWinType === 'REPEAT' ? 23 : fontSize * devicePixelRatio * 0.7}px ${
            segmentWinType === 'REPEAT' ? fontFamilyAlternative : fontFamily
        }`;
        ctx.fillText(segmentWinType === 'REPEAT' ? 'RESPIN' : 'FS', 0, (-2 * ratioSize) / 3 + 15, ratioSize / 2);
    };

    const segmentValueStyle = (ctx, segmentWinType, value) => {
        const reSpin = '\u21BB';
        ctx.font = `bold ${fontSize * devicePixelRatio * 1.25}px ${fontFamily}`;
        ctx.fillText(segmentWinType === 'REPEAT' ? reSpin : value, 0, (-3 * ratioSize) / 4, ratioSize / 2);
    };

    const drawSegment = (key, lastAngle, angle, canvasContext) => {
        const ctx = canvasContext;
        const value = (segments[key].winValue || 0).toString();
        const segmentColor = segments[key].color;
        const segmentWinType = segments[key].winType;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, ratioSize, lastAngle, angle, false);
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        const grd = ctx.createRadialGradient(centerX, centerY, 150, centerX, centerY, ratioSize);
        grd.addColorStop(0, adjustColor(segmentColor, 25));
        grd.addColorStop(0.5, segmentColor);
        grd.addColorStop(0.8, adjustColor(segmentColor, -30));
        grd.addColorStop(1, adjustColor(segmentColor, -50));
        ctx.fillStyle = grd;
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        ctx.fill();
        ctx.stroke();
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(Math.PI / segmentsLength + (key * 2 * Math.PI) / segmentsLength);
        ctx.fillStyle = fontColor;
        segmentValueStyle(ctx, segmentWinType, value);
        segmentNameStyle(ctx, segmentWinType, fontSize);
        ctx.restore();
    };

    const drawWheel = (canvasContext, angleCurrent = 0) => {
        const ctx = canvasContext;
        let lastAngle = angleCurrent;
        const PI2 = Math.PI * 2;
        ctx.lineWidth = 8;
        ctx.strokeStyle = 'black';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.font = `${ratioFontSize} ${fontFamily}`;
        ctx.fontWeight = 'bolder';

        for (let i = 1; i <= segmentsLength; i++) {
            const angle = PI2 * (i / segmentsLength) + angleCurrent;
            drawSegment(i - 1, lastAngle, angle, canvasContext);
            lastAngle = angle;
        }

        ctx.beginPath();
        ctx.arc(centerX, centerY, ratioSize, 0, PI2, false);
        ctx.closePath();
        ctx.fillStyle = '#000000';

        ctx.lineWidth = Math.floor(15 * devicePixelRatio);
        ctx.strokeStyle = '#ffffff';
        ctx.shadowColor = '#898';
        ctx.shadowBlur = 10;
        ctx.stroke();
    };

    const clear = canvasContext => {
        canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.width);
    };

    return (
        <div
            className={clsx('lucky-wheel-container flex flex-col', { mobile: onPhone })}
            style={{ visibility: isDrawing ? 'hidden' : 'visible' }}
        >
            <div className="lucky-wheel-heading">
                <h1 className="lucky-wheel-text-title text-center text-extra-bold pt-20 mb-10 uppercase">{title}</h1>
                <div className="lucky-wheel-line"></div>
                <div className="lucky-wheel-child fs-14 text-center pt-10">
                    {error ? i18n(`nui.lucky-wheel.${error}`) : subTitle}
                </div>
                <CloseIcon onClick={e => handleClose(e)} />
            </div>
            <div className={clsx('p-rel ml-auto mr-auto flex justify-center')}>
                <LuckyWheelNeedle width="35px" className={clsx('lucky-wheel-needle p-abs', { mobile: onPhone })} />
                <div className="lucky-wheel-button flex flex-col justify-center p-abs">
                    <img
                        src={luckyWheelButton}
                        alt="Lucky Wheel Button"
                        style={{
                            cursor: disabled || error ? 'default' : 'pointer'
                        }}
                        onClick={spin}
                    />
                </div>
                <div className="flex justify-center">
                    <canvas
                        className={clsx('canvas-wheel', { portal: !onPhone })}
                        ref={canvasRef}
                        width={countDimensions}
                        height={countDimensions}
                        style={{
                            pointerEvents: 'none',
                            ...countRotation(angle, spinTime)
                        }}
                    />
                </div>
            </div>
            <div className={clsx('lucky-wheel-child fs-14 text-center', { 'mt-30 pb-50': onPhone })}>
                <a
                    className="lucky-wheel-info text-center pb-30 fs-16 pointer"
                    onClick={() => (!disabled ? handleInfoClick() : null)}
                >
                    {i18n('nui.lucky-wheel.info')}
                </a>
            </div>
        </div>
    );
};

export default WheelComponent;
